import { useState, useEffect, useRef } from "react";
import { toast } from "react-toastify";
import { BiSolidLabel } from "react-icons/bi";
import { BsPersonSquare, BsPersonFillCheck } from "react-icons/bs";
import { FaEyeSlash } from "react-icons/fa";
import { IoMdDownload } from "react-icons/io";
import {
  MdFilterAltOff,
  MdLabel,
  MdClearAll,
  MdDelete,
  MdFilterAlt,
} from "react-icons/md";
import { RiSendPlane2Fill, RiChatOffFill } from "react-icons/ri";
import {
  Button,
  Dialog,
  DialogHeader,
  DialogBody,
  DialogFooter,
  Menu,
  MenuHandler,
  MenuList,
  MenuItem,
  Select as MTSelect,
  Option,
  Input,
} from "@material-tailwind/react";
import * as XLSX from "xlsx";

import { useUserContext } from "../../contexts/UserContextProvider";
import { useConversationContext } from "../../contexts/ConversationContextProvider";
import { Loader } from "..";
import { refreshMonitorData } from "../../data/messageNotifierServices";
import { contactLabels } from "../../data/contactLabels";
import { messageDateCheck } from "../utils/messageDateCheck";
import {
  getCurrentDateTime,
  convertUnixTimestampToDateTime,
} from "../utils/timeStampConversion";
import useSocket from "../utils/useSocket";
import { phoneNumberSanitation } from "../utils/inputSanitation";

const RecentChats = () => {
  const { socketConnection } = useSocket();
  const { currentUserInfo, apiCallGetUserAgents } = useUserContext();
  const {
    convGetChatContacts,
    recentContactsList,
    convGetChatHistory,
    selectedChatContactToShow,
    setSelectedChatContactToShow,
    setChatContact,
    setChatHistory,
    chatContact,
    updateMessageReadStatus,
    updateLabelChat,
    clearChat,
    deleteChat,
    isRecentChatLoaded,
    setRecentContactsList,
    currContact,
    setCurrContact,
  } = useConversationContext();

  const defaultFilterValues = {
    searchContact: "",
    searchName: "",
    selectedAgent: 0,
    selectedFromDate: "",
    selectedToDate: "",
    filterCriteria: "", // e.g. "unsaved", "not_replied", "interested"
  };

  const [filterBy, setFilterBy] = useState("");
  const [filterLabel, setFilterLabel] = useState("");
  const [openRightClickMenu, setOpenRightClickMenu] = useState(false);
  const [openFilterPopup, setOpenFilterPopup] = useState(false);
  const [openLabelPopup, setOpenLabelPopup] = useState(false);
  const [selectedLabels, setSelectedLabels] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [agentDetails, setAgentDetails] = useState([]);
  const [chatFilter, setChatFilter] = useState({
    searchContact: "",
    searchName: "",
    selectedAgent: 0,
    selectedFromDate: "",
    selectedToDate: "",
    filterCriteria: "", // e.g. "unsaved", "not_replied", "interested"
  });

  const updateFilter = (key, value) => {
    setChatFilter((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const divRef = useRef(null);

  let subscribe;

  const resetChatsFilters = async () => {
    setFilterBy("");
    setFilterLabel("");
    setChatFilter({ ...defaultFilterValues });

    setCurrentPage(1);
    setRecentContactsList([]);
    await convGetChatContacts(defaultFilterValues, 1);
  };

  const convertToLocalTime = (timestamp) => {
    const date = new Date(timestamp * 1000);
    return date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const handleCheckboxChange = (labelKey) => {
    setSelectedLabels((prevSelectedLabelsKey) => {
      // Check if the object is in the array
      const exists = prevSelectedLabelsKey.some(
        (item) => item.labelKey === labelKey
        //&& item.labelColor === labelColor
      );

      if (exists) {
        // Remove the object if it exists
        return prevSelectedLabelsKey.filter(
          (item) => item.labelKey !== labelKey
          //|| item.labelColor !== labelColor
        );
      } else {
        // Add the object if it doesn't exist
        return [...prevSelectedLabelsKey, { labelKey }];
      }
    });
  };

  const saveLabelOnChat = async () => {
    let selectedLabelsValue = [];
    selectedLabels?.map(
      (label) =>
        label?.labelKey != null && selectedLabelsValue.push(label.labelKey)
    );

    await updateLabelChat({
      labelValue: selectedLabelsValue,
      phoneNumber: selectedChatContactToShow,
    });
    setSelectedLabels([]);
    setOpenLabelPopup(false);
    await convGetChatContacts(defaultFilterValues, currentPage);
  };

  const isLabelSelected = (label) => {
    const isExists = selectedLabels.some(
      (item) => item.labelKey === label?.labelKey
    );
    return isExists;
  };

  const handleClearChat = async () => {
    await clearChat(selectedChatContactToShow);
    await convGetChatContacts(defaultFilterValues, currentPage);
    setSelectedChatContactToShow("");
    setChatContact("");
    setChatHistory(false);
  };

  const handleDeleteChat = async () => {
    const response = await deleteChat(selectedChatContactToShow);
    if (response) {
      await convGetChatContacts(
        defaultFilterValues,
        currentPage,
        selectedChatContactToShow
      );
    }
    setSelectedChatContactToShow("");
    setChatContact("");
    setChatHistory(false);
  };

  const handleContactFilter = (currentContact) => {
    if (filterBy === "unread") {
      if (!currentContact?.readStatus) return true;
      return false;
    } else if (filterBy === "label") {
      if (
        currentContact?.labels?.length > 0 &&
        currentContact?.labels?.some((item) => item === filterLabel)
      )
        return true;
      return false;
    } else if (filterBy === "replied") {
      if (currentContact?.messageData?.to) return true;
      return false;
    } else if (filterBy === "notReplied") {
      if (currentContact?.messageData?.from) return true;
      return false;
    } else if (filterBy === "assignedUser") {
      if (
        currentContact?.assignedUser &&
        JSON.parse(currentContact?.assignedUser).indexOf(
          currentUserInfo?.agentId
        ) !== -1
      )
        return true;
      return false;
    }
    return true;
  };

  const fetchChatOnClick = (contact) => {
    setChatContact(contact);
    if (contact._id) convGetChatHistory(contact._id);
    else {
      toast.warn("Couldn't fetch Chat of Invalid No.");
    }
  };

  const downloadRecentChatsOnClick = async () => {
    if (!recentContactsList) return;
    const filteredChats = recentContactsList?.filter((contact) =>
      handleContactFilter(contact)
    );
    //const filteredChats = recentContactsList;

    const mappedChats = filteredChats?.map((contact) => {
      const { fullName, profile, readStatus, _id, messageData } = contact;

      let lastMessageType = "unknown";
      if (messageData?.to) lastMessageType = "sent";
      else if (messageData?.from) lastMessageType = "received";

      return {
        full_name: fullName || "",
        profile_name: profile?.name || "",
        read_status: readStatus ? "Read" : "Unread",
        full_contact_number: _id || "",
        last_message_type: lastMessageType,
        message_type: messageData?.type || "",
        agent: messageData?.agent || "",
        timestamp: convertUnixTimestampToDateTime(messageData?.timestamp),
      };
    });

    const worksheet = XLSX.utils.json_to_sheet(mappedChats);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, filterBy || "Sheet1");

    XLSX.writeFile(
      workbook,
      `recent-chats ${getCurrentDateTime()} - ${filterBy || "all"}.xlsx`
    );
  };

  const updateReadStatusOfChat = (messageId) => {
    setRecentContactsList((prevContacts) => {
      let updatedContacts = [...prevContacts];
      let contactIndex = updatedContacts.findIndex((x) => x?._id === messageId);
      if (contactIndex !== -1) {
        updatedContacts[contactIndex].readStatus = true;
      }
      return updatedContacts;
    });
  };

  const socketMessageListener = async (messageData, messageReadStatus) => {
    let msg =
      messageData?.type === "messageReceived"
        ? messageData?.message?.messaging_product
        : messageData;

    let messageId =
      messageData?.type === "messageReceived"
        ? msg?.messages?.[0]?.from
        : msg?.message?.recipient_id;

    let messageDataContent =
      messageData?.type === "messageReceived" ? msg?.messages?.[0] : null;

    let readStatus = messageReadStatus ?? false;

    setRecentContactsList((prevContacts) => {
      let updatedContacts = [...prevContacts];
      let contactIndex = updatedContacts.findIndex((x) => x?._id === messageId);
      if (contactIndex !== -1) {
        // Remove contact from previous position
        let existingContact = updatedContacts.splice(contactIndex, 1)[0];
        // Update message data and read status
        updatedContacts.unshift({
          ...existingContact,
          messageData: messageDataContent ?? existingContact.messageData,
          readStatus: readStatus ?? existingContact.readStatus,
          profile: existingContact.profile, // No unnecessary profile override
        });
      } else {
        // Fetch chat contacts if not found
        convGetChatContacts(chatFilter, currentPage);
      }

      return updatedContacts;
    });
  };

  const handleRecentChatsOnScroll = () => {
    if (
      divRef.current.scrollTop + divRef.current.clientHeight >=
        divRef.current.scrollHeight - 80 &&
      isRecentChatLoaded === false
    ) {
      setCurrentPage((prevPage) => prevPage + 1);
    }
  };

  const loadRecentChats = async () => {
    await convGetChatContacts(chatFilter, currentPage);
  };

  useEffect(() => {
    if (socketConnection?.connected) {
      socketConnection.on(currentUserInfo?.wabPhoneId, socketMessageListener);
    }
    return () => {
      socketConnection.off(currentUserInfo?.wabPhoneId, socketMessageListener);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatContact]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    subscribe = refreshMonitorData?.subscribe(async (messageData) => {
      const data = messageData?.msg;
      let selectedContact = messageData?.selectedContact;

      if (data !== "refreshRecentChat") {
        setTimeout(() => {
          let messageReadStatus = false;
          const msg = data?.messaging_product;
          if (msg?.messages?.[0]?.from === selectedContact) {
            messageReadStatus = true;
          }
          socketMessageListener(
            {
              type: "messageReceived",
              message: data,
            },
            messageReadStatus
          );
        }, 400);
      }
    });

    return () => {
      subscribe?.unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatFilter]);

  useEffect(() => {
    if (currentPage === 1) {
      setRecentContactsList([]);
    }
    loadRecentChats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  useEffect(() => {
    const div = divRef.current;
    if (div) {
      div.addEventListener("scroll", handleRecentChatsOnScroll);
    }
    return () => {
      if (div) {
        div.removeEventListener("scroll", handleRecentChatsOnScroll);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRecentChatLoaded, currentPage]);

  return (
    <main className="bg-white w-full h-[40vh] md:h-screen px-1 pb-2 md:col-span-3 lg:col-span-2 drop-shadow-md overflow-y-auto">
      <div className="m-1 flex justify-between gap-1">
        <div className="w-fit space-x-1">
          <Menu>
            <MenuHandler>
              <button
                type="button"
                className="my-button-style-4 bg-green-300 text-green-900 hover:text-black"
              >
                <MdLabel title="Filter Label" size={20} />
              </button>
            </MenuHandler>
            <MenuList className="absolute left-0 mt-2 bg-white shadow-md max-h-72 space-y-1 z-50">
              {contactLabels?.map((lbl) => (
                <MenuItem
                  key={lbl?.labelKey}
                  onClick={() => {
                    setFilterBy("label");
                    setFilterLabel(lbl?.labelKey);
                  }}
                  className="hover:bg-blue-300 focus:bg-blue-300"
                  style={{
                    borderLeft: "7px solid",
                    borderLeftColor: lbl?.labelColor,
                  }}
                >
                  {lbl?.label}
                </MenuItem>
              ))}
            </MenuList>
          </Menu>
          <button
            type="button"
            onClick={() => setFilterBy("unread")}
            title="Unread"
            className={`my-button-style-4 ${
              filterBy === "unread"
                ? "bg-black text-white hover:text-white"
                : "bg-green-300 text-green-900 hover:text-black"
            }`}
          >
            <FaEyeSlash size={20} />
          </button>
          <button
            type="button"
            onClick={() => setFilterBy("replied")}
            title="Replied to"
            className={`my-button-style-4 ${
              filterBy === "replied"
                ? "bg-black text-white hover:text-white"
                : "bg-green-300 text-green-900 hover:text-black"
            }`}
          >
            <RiSendPlane2Fill size={20} />
          </button>
          <button
            type="button"
            onClick={() => setFilterBy("notReplied")}
            title="Not replied to"
            className={`my-button-style-4 ${
              filterBy === "notReplied"
                ? "bg-black text-white hover:text-white"
                : "bg-green-300 text-green-900 hover:text-black"
            }`}
          >
            <RiChatOffFill size={20} />
          </button>
          <button
            type="button"
            onClick={() => setFilterBy("assignedUser")}
            title="Assigned to you"
            className={`my-button-style-4 ${
              filterBy === "assignedUser"
                ? "bg-black text-white hover:text-white"
                : "bg-green-300 text-green-900 hover:text-black"
            }`}
          >
            <BsPersonFillCheck size={20} />
          </button>
          <button
            type="button"
            onClick={() => resetChatsFilters()}
            title="Clear Filter"
            className={`my-button-style-4 ${
              filterBy === ""
                ? "bg-black text-white hover:text-white"
                : "bg-green-300 text-green-900 hover:text-black"
            }`}
          >
            <MdFilterAltOff size={20} />
          </button>
          <button
            type="button"
            onClick={async () => {
              if (agentDetails || agentDetails.length === 0) {
                const response = await apiCallGetUserAgents();
                if (response && response.length > 0) {
                  let agents = [];
                  agents.push({ agentId: 0, agentUsername: "None" });
                  let filterAgent = response?.filter((x) => x.isBlocked === 0);
                  agents.push(...filterAgent);
                  setAgentDetails(agents);
                  setOpenFilterPopup(true);
                }
              } else {
                setOpenFilterPopup(true);
              }
            }}
            title="Advance Search Filter"
            className={`my-button-style-4 bg-wabmBlue text-white hover:text-black inline-flex gap-1`}
          >
            <MdFilterAlt size={20} /> Advance
          </button>
        </div>
        <div className="inline-flex items-center gap-1">
          <p title="No. of recent chats loaded" className="text-sm italic">
            {
              recentContactsList?.filter((contact) =>
                handleContactFilter(contact)
              )?.length
            }
          </p>
          <button
            type="button"
            title="Download Filtered Chats"
            onClick={() => downloadRecentChatsOnClick()}
          >
            <IoMdDownload size={24} />
          </button>
        </div>
      </div>
      <div>
        {recentContactsList?.length === 0 ? (
          <Loader />
        ) : (
          <div
            ref={divRef}
            style={{
              height: "90vh",
              overflowY: "scroll",
              border: "1px solid black",
            }}
          >
            {recentContactsList?.[0]?.isEmpty ? (
              <div className="grid place-content-center text-green-900 text-center">
                <p>Wow, so empty!</p>
                <p>Start a Campaign or Chat Now.</p>
              </div>
            ) : (
              recentContactsList?.map(
                (contact) =>
                  handleContactFilter(contact) && (
                    <button
                      key={contact?._id}
                      title="Right click to view more options"
                      onContextMenu={(e) => {
                        e.preventDefault();
                        setCurrContact(contact);
                        setSelectedChatContactToShow(contact?._id);
                        setOpenRightClickMenu(!openRightClickMenu);
                      }}
                      onClick={(e) => {
                        e.preventDefault();
                        if (
                          (contact?.assignedUser &&
                            JSON.parse(contact?.assignedUser).indexOf(
                              currentUserInfo?.agentId
                            ) !== -1) ||
                          currentUserInfo?.canViewChat === 1 ||
                          currentUserInfo?.showUnassignedChat === 1 ||
                          currentUserInfo?.isAdmin === 1 ||
                          currentUserInfo?.isAdmin === 2
                        ) {
                          setChatHistory(true);
                          setSelectedChatContactToShow(contact?._id);
                          fetchChatOnClick(contact);
                          updateReadStatusOfChat(contact?._id);
                        } else {
                          toast.warning(
                            "You are not authorized to open this chat"
                          );
                        }
                      }}
                      className={`my-smooth-transition-1 bg-green-400 bg-opacity-20 w-full h-16 mb-[2px] pl-5 relative flex items-center gap-5 rounded-sm ${
                        contact?._id === selectedChatContactToShow
                          ? "bg-opacity-50"
                          : "hover:bg-opacity-50"
                      }`}
                    >
                      <div className="absolute top-2 right-2 flex items-center gap-2">
                        {contact?.labels &&
                          contact?.labels?.map((labelKey) => {
                            const getLabelByKey = (key) =>
                              contactLabels.find(
                                (label) => label.labelKey === key
                              );

                            const label = getLabelByKey(labelKey);

                            return (
                              <BiSolidLabel
                                key={labelKey}
                                title={label?.label || ""}
                                style={{ color: label?.labelColor ?? "gray" }}
                                className="w-5 h-5"
                              />
                            );
                          })}
                        {contact?.messageData?.agent ===
                          currentUserInfo?.agentName && (
                          <RiSendPlane2Fill
                            title="You replied to this chat at last"
                            size={20}
                            className="text-green-900"
                          />
                        )}
                        {contact?.assignedUser &&
                          JSON.parse(contact?.assignedUser).indexOf(
                            currentUserInfo?.agentId
                          ) !== -1 && (
                            <BsPersonFillCheck
                              title="This chat is assigned to you!"
                              size={20}
                              className="text-infokeyYellow"
                            />
                          )}
                      </div>
                      <BsPersonSquare
                        size={32}
                        className={`text-green-700 ${
                          contact?._id === selectedChatContactToShow &&
                          "text-green-900"
                        }`}
                      />
                      <div className="text-sm">
                        <span className="flex gap-1 flex-wrap">
                          {contact?.fullName?.length > 1 ? (
                            <h3>{contact?.fullName?.substring(0, 40)}</h3>
                          ) : (
                            `+${contact?._id}`
                          )}
                        </span>
                        {contact?.messageData?.type === "text" && (
                          <p className="text-green-900 text-sm text-left">
                            {typeof contact?.messageData?.text?.body !==
                            "string"
                              ? String(
                                  contact?.messageData?.text?.body
                                )?.substring(0, 25)
                              : contact?.messageData?.text?.body?.substring(
                                  0,
                                  25
                                )}
                          </p>
                        )}
                        {contact?.messageData?.type === "template" && (
                          <p className="text-green-900 text-sm text-left uppercase">
                            {contact?.messageData?.template?.name?.substring(
                              0,
                              25
                            ) + "..."}
                          </p>
                        )}
                        {contact?.messageData?.type === "button" && (
                          <p className="text-green-900 text-sm text-left uppercase">
                            {"< " + contact?.messageData?.button?.text}
                          </p>
                        )}
                        {contact?.messageData?.type === "unsupported" && (
                          <p className="text-green-900 text-sm text-left uppercase">
                            {">"} Unsupported message
                          </p>
                        )}
                      </div>
                      <div
                        className={`absolute bottom-1 right-1 flex items-center gap-2 ${
                          contact?.readStatus
                            ? "text-green-900"
                            : "text-amber-500"
                        } text-xs`}
                      >
                        {!contact?.readStatus && (
                          <span
                            title="This message is unread"
                            className="bg-amber-500 w-2 h-2 rounded-full"
                          />
                        )}
                        <p>
                          {messageDateCheck(
                            contact?.messageData?.timestamp
                          ).toString() === "Today"
                            ? convertToLocalTime(
                                contact?.messageData?.timestamp
                              )
                            : messageDateCheck(
                                contact?.messageData?.timestamp
                              ).toString()}
                        </p>
                      </div>
                    </button>
                  )
              )
            )}
            {isRecentChatLoaded && <Loader />}
          </div>
        )}
      </div>

      <Dialog
        className="w-48"
        open={openFilterPopup}
        handler={() => setOpenFilterPopup(!openFilterPopup)}
      >
        <DialogHeader>Advance Search Filter</DialogHeader>

        <DialogBody>
          <div className="flex flex-col justify-start gap-5">
            <Input
              label="Phone No."
              variant="standard"
              color="blue"
              labelProps={{
                className: "text-slate-800",
              }}
              required
              placeholder="Search by phone number"
              value={chatFilter?.searchContact}
              onChange={(e) =>
                updateFilter(
                  "searchContact",
                  phoneNumberSanitation(e.target.value.replace("+", ""))
                )
              }
              className="my-input-box-1"
              autoComplete="off"
            />
            <Input
              label="Name"
              variant="standard"
              color="blue"
              labelProps={{
                className: "text-slate-800",
              }}
              required
              placeholder="Search by name"
              value={chatFilter?.searchName}
              onChange={(e) =>
                updateFilter("searchName", e.target.value.replace("+", ""))
              }
              className="my-input-box-1"
              autoComplete="off"
            />

            <MTSelect
              variant="standard"
              color="blue"
              label="Select Admin/Agent to filter assigned chat"
              value={chatFilter.selectedAgent}
              onChange={(val) => updateFilter("selectedAgent", val)}
              className="my-input-box-1 pt-3"
            >
              {agentDetails?.map((item) => (
                <Option
                  key={item?.agentId}
                  value={item?.agentId}
                  className="hover:bg-blue-300 focus:bg-blue-300"
                >
                  {item?.agentUsername}
                </Option>
              ))}
            </MTSelect>
          </div>
        </DialogBody>

        <DialogFooter>
          <div className="flex flex-row justify-end gap-2">
            <Button
              onClick={async () => {
                setCurrentPage(1);
                setRecentContactsList([]);
                await loadRecentChats();
                setOpenFilterPopup(false);
              }}
              variant="filled"
              color="green"
            >
              Apply Filter
            </Button>
            <Button
              onClick={async () => {
                setChatFilter({ ...defaultFilterValues });
              }}
              variant="outlined"
              color="black"
            >
              Clear Filter
            </Button>
          </div>
        </DialogFooter>
      </Dialog>

      <Dialog
        open={openLabelPopup}
        handler={() => setOpenLabelPopup(!openLabelPopup)}
      >
        <DialogHeader>
          <div className="space-y-1">
            <h3 className="text-lg font-bold">Select Label</h3>
            <p>Mark your chat with the following labels</p>
          </div>
        </DialogHeader>
        <DialogBody>
          <div className="flex flex-col gap-2">
            {contactLabels?.map((label) => (
              <div
                key={label.labelKey}
                className="flex flex-row gap-2 items-center"
              >
                <input
                  type="checkbox"
                  checked={isLabelSelected(label)}
                  onChange={() => handleCheckboxChange(label.labelKey)}
                />
                <BiSolidLabel
                  className="w-8 h-8"
                  style={{ color: label.labelColor }}
                />
                <div>{label.label}</div>
              </div>
            ))}
          </div>
        </DialogBody>
        <DialogFooter>
          <Button
            onClick={() => {
              saveLabelOnChat();
            }}
            variant="filled"
            color="green"
          >
            Confirm
          </Button>
        </DialogFooter>
      </Dialog>

      <Dialog
        open={openRightClickMenu}
        handler={() => setOpenRightClickMenu(!openRightClickMenu)}
      >
        <DialogHeader>
          <div className="space-y-1">
            <h3 className="text-lg font-bold">
              Options for
              {currContact?.fullName?.length > 1 ? (
                <span className="font-bold text-xl ml-2 text-green-900">
                  {currContact?.fullName?.substring(0, 40)}
                </span>
              ) : (
                <span className="font-bold text-xl ml-2 text-green-900">
                  {" +" + currContact?._id}
                </span>
              )}
            </h3>
            <p>Select an action you want to perform on the selected chat</p>
          </div>
        </DialogHeader>
        <DialogBody className="flex gap-5">
          <Button
            type="button"
            color="black"
            onClick={() => {
              updateMessageReadStatus(currContact?._id);
              setOpenRightClickMenu(false);
            }}
          >
            Mark as unread
          </Button>
          <Button
            type="button"
            color="blue"
            onClick={() => {
              setSelectedChatContactToShow(currContact?._id);
              let chatLabels = [];
              currContact?.labels?.map((label) =>
                chatLabels.push({
                  labelKey: label,
                })
              );
              setSelectedLabels(chatLabels);
              setOpenLabelPopup(true);
              setOpenRightClickMenu(false);
            }}
            className="flex gap-1"
          >
            <MdLabel /> Label Chat
          </Button>
          <Button
            type="button"
            color="purple"
            onClick={() => {
              setSelectedChatContactToShow(currContact?._id);
              handleClearChat();
              setOpenRightClickMenu(false);
              setOpenLabelPopup(false);
            }}
            className="flex gap-1"
          >
            <MdClearAll /> Clear Chat
          </Button>
          <Button
            type="button"
            color="red"
            onClick={() => {
              setSelectedChatContactToShow(currContact?._id);
              handleDeleteChat();
              setOpenRightClickMenu(false);
              setOpenLabelPopup(false);
            }}
            className="flex gap-1"
          >
            <MdDelete /> Delete Chat
          </Button>
        </DialogBody>
      </Dialog>
    </main>
  );
};

export default RecentChats;
